import React from 'react';
import { QueryHookOptions } from '@apollo/client';
import {
  FragmentData,
  OperationData,
  OperationVariables,
  gql,
} from '@ts-gql/tag';
import { useQuery } from '@ts-gql/apollo';

import { getRecurrenceFrequency } from '../globals';
import {
  useTenantTimezoneConversion,
  utcToTenantToLocalDateFunction,
} from '../hooks/use-tenant-timezone';

export const fragment = gql`
  fragment Job_RecurrenceFragment on Job {
    recurrence {
      cloudId
      nextDateUtc
      enabled
      recurrenceFrequency
    }
  }
` as import('../../__generated__/ts-gql/Job_RecurrenceFragment').type;

export type JobRecurrenceFragment = FragmentData<typeof fragment>;

export const GET_JOB_QUERY = gql`
  query GetJob($cloudId: String!) {
    getJob(cloudId: $cloudId) {
      cloudId
      WfModifiedBy
      WfModifiedOnUtc
      tenantId
      name
      clientId
      staffUserId
      managerUserId
      partnerUserId
      assigneeUserId
      jobStatusId
      dueDate
      completedDate
      closedDate
      startDate
      budget
      signOffRequestedFromUserId
      signOffActionedByUserId
      signOffActionedDateUtc
      signOffRequestedByUserId
      signOffRequestedDateUtc
      signOffStatus
      templateId
      WfDeletedBy
      WfDeletedOnUTC
      WfCreatedBy
      WfCreatedOnUTC
      entities
      jobRecurrenceId
      ...Job_RecurrenceFragment
    }
  }
` as import('../../__generated__/ts-gql/GetJob').type;

export type GetJobQueryVariables = OperationVariables<typeof GET_JOB_QUERY>;
export type GetJobQueryResult = OperationData<typeof GET_JOB_QUERY>;

export type GetJobResult = NonNullable<ReturnType<typeof processData>>;
export type JobRecurrenceResult = NonNullable<GetJobResult['recurrence']>;

export const useGetJob = (
  options: QueryHookOptions<GetJobQueryResult, GetJobQueryVariables>
) => {
  const variables = options.variables as GetJobQueryVariables;

  const res = useQuery(GET_JOB_QUERY, {
    context: { service: 'core' },
    variables,
    ...options,
  });

  const { utcToTenantToLocalDate, loading } = useTenantTimezoneConversion();

  return React.useMemo(() => {
    if (loading) {
      return {
        loading: true,
        error: undefined,
        data: undefined,
        refetch: undefined,
      };
    }

    const { data } = res;
    return {
      ...res,
      data: data ? processData(data, utcToTenantToLocalDate) : null,
    };
  }, [res, utcToTenantToLocalDate, loading]);
};

export function processData(
  data: GetJobQueryResult,
  dateConvert: utcToTenantToLocalDateFunction
) {
  const job = data?.getJob;

  const recurrence =
    (job?.recurrence && {
      ...job.recurrence,
      recurrenceFrequency: getRecurrenceFrequency(
        job.recurrence.recurrenceFrequency
      ),
      nextDateUtc: dateConvert(job.recurrence.nextDateUtc),
    }) ||
    undefined;

  const result = job && {
    ...job,
    startDateRaw: job.startDate,
    startDate: dateConvert(job.startDate),
    dueDateRaw: job.dueDate,
    dueDate: dateConvert(job.dueDate),
    completedDate: job.completedDate && new Date(job.completedDate),
    closedDate: job.closedDate && new Date(job.closedDate),
    WfCreatedOnUTC: job.WfCreatedOnUTC && new Date(job.WfCreatedOnUTC),
    recurrence,
  };
  return result;
}
